import {
	Box,
	Button,
	HStack,
	Image,
	Text,
	useDisclosure,
} from "@chakra-ui/react";
import { parse } from "@wordpress/blocks";
import { useDispatch, useSelect } from "@wordpress/data";
import { __ } from "@wordpress/i18n";
import { store as noticesStore } from "@wordpress/notices";
import React from "react";
import { useMutation } from "react-query";
import axios from "redaxios";
import { saveImageLocally } from "../../helpers";
import { preferencesStore } from "../../store/preferences-store";

const LibraryItem = (props: {
	id: number;
	imageUrl: string;
	onClose: () => void;
	onClick?: () => void;
	title: string;
	slug: string;
	type: string;
	isStarter: boolean;
}) => {
	const favorites = useSelect(
		(select) => select(preferencesStore).getFavorites() as string[],
		[]
	);
	const { updateFavorites } = useDispatch("magazine-blocks/preferences");
	const { createSuccessNotice, createErrorNotice } =
		useDispatch(noticesStore);
	const dynamicBlocks = [
		"magazine-blocks/banner-posts",
		"magazine-blocks/grid-module",
		"magazine-blocks/featured-categories",
		"magazine-blocks/featured-posts",
		"magazine-blocks/post-list",
		"magazine-blocks/post-video",
		"magazine-blocks/slider",
		"magazine-blocks/grid-module",
	];
	const { mutate, isLoading } = useMutation(
		async (_id: number) => {
			try {
				const response = await axios.get(
					`https://blocks.themegrilldemos.com/wp-json/blockart-library/v1/mzb/content?id=${_id}`
				);
				const content = await saveImageLocally(response?.data ?? "");
				//@ts-ignore
				const blocks = parse(content, {
					__unstableSkipMigrationLogs: true,
				});

				function processBlock(block: any) {
					// Check if it's a dynamic block
					if (dynamicBlocks.includes(block.name)) {
						block.attributes.category = undefined;
						block.attributes.tag = undefined;
					}

					// If it has inner blocks, recursively process each one
					if (block.innerBlocks && block.innerBlocks.length > 0) {
						block.innerBlocks.forEach(processBlock);
					}
				}

				blocks.forEach(processBlock);

				wp.data.dispatch("core/block-editor").insertBlocks(blocks);
				return _id;
			} catch {
				throw Error("Failed to import");
			}
		},
		{
			onSuccess() {
				createSuccessNotice(__("Import success!", "magazine-blocks"), {
					type: "snackbar",
				});
				props.onClose();
			},
			onError() {
				createErrorNotice(__("Import failed!", "magazine-blocks"), {
					type: "snackbar",
				});
				props.onClose();
			},
		}
	);
	const { isOpen, onOpen, onClose } = useDisclosure({
		id: `ba-${props.id}`,
	});
	if (props.isStarter) {
		return (
			<Box
				borderRadius="sm"
				bgColor="white"
				border="1px"
				borderColor="gray.200"
				onClick={props.onClick}
				cursor="pointer"
			>
				<Box p="12px" position="relative">
					<Image src={props.imageUrl} w="full" />
				</Box>
				<HStack justify="space-between" p="12px">
					<Text as="span">{props.title}</Text>
				</HStack>
			</Box>
		);
	}
	return (
		<Box
			borderRadius="sm"
			bgColor="white"
			key={props.id}
			onClick={props.onClick}
			border="1px"
			borderColor="gray.200"
			onMouseEnter={onOpen}
			onMouseLeave={onClose}
		>
			<Box p="12px" position="relative">
				<Image src={props.imageUrl} w="full" />
				<Box
					position="absolute"
					top={0}
					left={0}
					h="full"
					w="full"
					bgColor="rgba(0, 0, 0, 0.30)"
					zIndex={1}
					display="grid"
					placeItems="center"
					visibility={isOpen || isLoading ? "visible" : "hidden"}
					pointerEvents={isOpen || isLoading ? "auto" : "none"}
					opacity={isOpen || isLoading ? 1 : 0}
					transition="opacity 0.2s ease-in-out"
				>
					<Button
						isLoading={isLoading}
						fontSize="small"
						onClick={() => mutate(props.id)}
						size="sm"
						fontWeight="600"
						leftIcon={
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="18"
								height="18"
								viewBox="0 0 15 15"
								fill="none"
							>
								<path
									// eslint-disable-next-line max-len
									d="M11.6096 11.1684C11.4783 11.1691 11.3505 11.1258 11.2466 11.0455C11.1427 10.9652 11.0686 10.8525 11.0361 10.7253C11.0036 10.598 11.0146 10.4636 11.0673 10.3433C11.12 10.223 11.2114 10.1238 11.327 10.0614C11.7495 9.82988 12.0629 9.44025 12.1985 8.97797C12.2689 8.74937 12.2922 8.50884 12.2669 8.27098C12.2416 8.03312 12.1682 7.80288 12.0513 7.59421C11.8958 7.30669 11.6653 7.0667 11.3844 6.89976C11.1034 6.73282 10.7824 6.64519 10.4555 6.64619H9.86669C9.73297 6.65042 9.6018 6.60899 9.49477 6.52872C9.38774 6.44845 9.31123 6.33412 9.27786 6.20456C9.17209 5.78861 8.98529 5.39761 8.72815 5.05398C8.47101 4.71034 8.14859 4.42083 7.77936 4.20203C7.41013 3.98323 7.00135 3.83943 6.57645 3.7789C6.15155 3.71836 5.71888 3.74226 5.30324 3.84923C4.88728 3.95499 4.49628 4.1418 4.15265 4.39894C3.80902 4.65608 3.5195 4.9785 3.3007 5.34773C3.0819 5.71696 2.93811 6.12574 2.87757 6.55064C2.81703 6.97554 2.84093 7.40821 2.94791 7.82385C3.07996 8.32159 3.32361 8.78272 3.66039 9.17228C3.76332 9.28947 3.81555 9.44271 3.80562 9.59837C3.79568 9.75403 3.72439 9.89938 3.6074 10.0025C3.49021 10.1055 3.33697 10.1577 3.18131 10.1478C3.02565 10.1378 2.8803 10.0665 2.77714 9.94954C2.31568 9.4227 1.98306 8.79577 1.80557 8.11827C1.47998 7.00209 1.60523 5.80272 2.15442 4.77789C2.70361 3.75307 3.63291 2.98457 4.74262 2.63755C5.85232 2.29052 7.05388 2.39266 8.08909 2.92202C9.1243 3.45138 9.91054 4.3657 10.2789 5.46852H10.4555C11.12 5.4702 11.7651 5.69263 12.2893 6.10086C12.8136 6.50908 13.1874 7.07996 13.3518 7.72374C13.5163 8.36752 13.4622 9.04771 13.198 9.65739C12.9338 10.2671 12.4745 10.7717 11.8923 11.0919C11.806 11.1408 11.7088 11.1671 11.6096 11.1684ZM9.91968 9.49025C9.80936 9.38058 9.66012 9.31902 9.50456 9.31902C9.34899 9.31902 9.19975 9.38058 9.08943 9.49025L8.15319 10.4442V7.5C8.15319 7.34383 8.09115 7.19406 7.98072 7.08363C7.87029 6.9732 7.72052 6.91116 7.56435 6.91116C7.40819 6.91116 7.25841 6.9732 7.14799 7.08363C7.03756 7.19406 6.97552 7.34383 6.97552 7.5V10.4442L6.05694 9.49025C6.00192 9.43568 5.93667 9.3925 5.86493 9.3632C5.79319 9.33389 5.71636 9.31904 5.63887 9.31949C5.56138 9.31904 5.48456 9.33389 5.41281 9.3632C5.34107 9.3925 5.27582 9.43568 5.2208 9.49025C5.16483 9.54421 5.1201 9.60871 5.08919 9.68005C5.05828 9.75138 5.04181 9.82813 5.04071 9.90587C5.03962 9.9836 5.05393 10.0608 5.08281 10.133C5.1117 10.2051 5.1546 10.2709 5.20902 10.3264L7.14039 12.2519C7.19465 12.3048 7.25864 12.3468 7.32882 12.3755C7.40011 12.4075 7.47737 12.4241 7.55552 12.4241C7.63367 12.4241 7.71093 12.4075 7.78222 12.3755C7.85241 12.3468 7.91639 12.3048 7.97065 12.2519L9.91968 10.3264C9.97487 10.2717 10.0187 10.2065 10.0486 10.1348C10.0785 10.063 10.0939 9.98605 10.0939 9.90832C10.0939 9.83059 10.0785 9.75362 10.0486 9.68187C10.0187 9.61011 9.97487 9.54499 9.91968 9.49025Z"
									fill="white"
								/>
							</svg>
						}
					>
						{__("Import", "magazine-blocks")}
					</Button>
				</Box>
			</Box>
			<HStack justify="space-between" p="12px">
				<Text as="span">{props.title}</Text>
				<Button
					variant="icon"
					sx={{
						svg: {
							fill: favorites.includes(`${props.slug}`)
								? "primary.500"
								: "#fff",
							stroke: favorites.includes(`${props.slug}`)
								? "primary.500"
								: "gray.400",
						},
					}}
					onClick={() => {
						updateFavorites(
							favorites.includes(`${props.slug}`)
								? favorites.filter((f) => f !== `${props.slug}`)
								: [...favorites, `${props.slug}`]
						);
					}}
				>
					<svg
						stroke="currentColor"
						fill="none"
						strokeWidth="2"
						viewBox="0 0 24 24"
						strokeLinecap="round"
						strokeLinejoin="round"
						height="16px"
						width="16px"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"></path>
					</svg>
				</Button>
			</HStack>
		</Box>
	);
};

export default LibraryItem;
